// DVA Daily Live Price (Wtd. Average) — the public readers.
//
// Owner, 2026-10-07, relaying Rob: "the same thing for our live index that
// isn't 7 day rolling average… our live number that changes throughout the
// day". What one sale day's qualifying lots brought, head-weighted, per index
// class — the same lots and rules as the index, one day instead of seven.
// RB_DAILY_PRICE_PLAN.md.
//
// ⚠️ NEVER THE SUPABASE CLIENT. This is read by the prerendered homepage,
// which renders under Node; everything goes through /api/market (the
// allowlist in api/market.ts), like rbIndexes.ts.
//
// ⚠️ NOT THE INDEX. The index is the seven-day benchmark. Codes here carry
// "-D" and nothing from this file is ever shown in a table with the index.

import { rpc, rpcValue } from "@/lib/rbCattleTicker";
import { HIDDEN_INDEX_SLUGS } from "@/lib/rbIndexes";
import { tickerAuthSettled } from "@/lib/tickerAuth";

/** The indexes the owner has taken off every screen (rbIndexes.ts) leave the
 *  daily board and grid too — hiding them only on the cards left the 500+
 *  on the homepage's Live Prices view (found 2026-10-13). */
const shown = (r: Record<string, unknown>) => !HIDDEN_INDEX_SLUGS.has(String(r.slug ?? ""));

function num(v: unknown): number | null {
  if (v === null || v === undefined || v === "") return null;
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}

export type DailyDays = 30 | 90 | 400;
export const DAILY_RANGES: ReadonlyArray<readonly [string, DailyDays]> = [["1M", 30], ["3M", 90], ["Record", 400]];

/** The three toggles on the homepage board (owner: "toggles for each index category"). */
export type DailyCategory = "headline" | "steers" | "heifers";
export const DAILY_CATEGORIES: ReadonlyArray<{ key: DailyCategory; label: string }> = [
  { key: "headline", label: "RaisedBeef Daily Live Indexes" }, // owner, 2026-10-08
  { key: "steers", label: "Steer Indexes by Weight" },   // owner, 2026-10-08
  { key: "heifers", label: "Heifer Indexes by Weight" },
];

export interface DailyQuote {
  slug: string;
  /** The index code with "-D": DVAFS-D. */
  code: string;
  name: string;
  isHeadline: boolean;
  cohort: string;
  cattleType: string | null;
  /** The sale day the figure describes — today while live, else the last written day. */
  asOf: string;
  /** True when `last` is a written final value (shown with an "s"); false while today is still building. */
  settled: boolean;
  last: number | null;
  change: number | null;
  changePct: number | null;
  open: number | null;
  high: number | null;
  low: number | null;
  prev: number | null;
  prevAsOf: string | null;
  /** The same weekday last week: its final, the change against it, its head and the head change in percent. */
  wkAsOf: string | null;
  wkCwt: number | null;
  changeWk: number | null;
  changeWkPct: number | null;
  wkHead: number | null;
  headChgWkPct: number | null;
  head: number;
  lots: number;
  barns: number;
  states: number;
  /** Today's lots so far, whatever the row shows — so a row showing yesterday can say why. */
  todayHead: number;
  todayLots: number;
  todayPublication: "PUBLISHED" | "INSUFFICIENT_VOLUME" | "NO_SALES" | null;
  floor: number | null;
  status: string;
}

export interface DailyHistoryRow {
  asOf: string;
  sunday: boolean;
  /** Null on a Sunday, a day under the floor, or a day with no sales — a dash row. */
  price: number | null;
  change: number | null;
  changePct: number | null;
  prev: number | null;
  open: number | null;
  high: number | null;
  low: number | null;
  head: number | null;
  lots: number | null;
  barns: number | null;
  states: number | null;
  headWkAgo: number | null;
  headChgWk: number | null;
  headChgWkPct: number | null;
  publication: "PUBLISHED" | "INSUFFICIENT_VOLUME" | "NO_SALES" | "SUNDAY";
  revision: number | null;
  stepMinutes: number | null;
  /** Lots that day with no arrival time (counted from the first reading) and lots reported after the day ended (in the final value, in no reading). */
  unknownArrivalLots: number;
  lateLots: number;
  /** The r2's one line: what the final was before this version, when it changed, why. Null on version 1. */
  beforeCwt: number | null;
  revisedAt: string | null;
  revisionReason: string | null;
}

/** One landing on the day's path: a moment a qualifying lot (or several in the
 *  same second) reached us, and what the running average became. Exact, not
 *  sampled. */
export interface DailyTick {
  at: string;
  price: number;
  change: number | null;
  head: number;
  /** Head that landed at this moment. */
  headChange: number | null;
  lots: number;
  lotsAdded: number;
  /** The lot carried no arrival time and is counted from the start of the day. */
  untimed: boolean;
  /** Reported after the day ended: in the day's final, shown with its real time. */
  late: boolean;
  stepMinutes: number;
}

/** One index, one day, start to finish. */
export interface DailyPath {
  slug: string;
  code: string;
  name: string;
  asOf: string;
  sunday: boolean;
  /** True while the day is on: the page polls and appends. */
  live: boolean;
  /** The head a day needs before it has a price: 10, or 1 for cows and bulls. */
  floor: number | null;
  open: number | null;
  high: number | null;
  low: number | null;
  last: number | null;
  firstAt: string | null;
  lastAt: string | null;
  marks: number | null;
  stepMinutes: number | null;
  unknownArrivalLots: number;
  lateLots: number;
  final: number | null;
  finalHead: number | null;
  finalLots: number | null;
  finalBarns: number | null;
  finalPublication: string | null;
  finalRevision: number | null;
  ticks: DailyTick[];
}

/** The reader returns ONE object, not rows. `rpc()` reads arrays only and
 *  hands an object back as nothing — which is exactly how the day page read
 *  "No such index" on the owner's phone the minute it shipped (2026-10-07).
 *  `rpcValue` with a guard is the helper for a single value. */
const isPath = (v: unknown): v is Record<string, unknown> =>
  !!v && typeof v === "object" && !Array.isArray(v) && typeof (v as Record<string, unknown>).slug === "string";

export async function fetchDailyPath(slug: string, asOf: string): Promise<DailyPath | null> {
  await tickerAuthSettled();
  const r = await rpcValue<Record<string, unknown>>("rb_daily_price_path", { p_slug: slug, p_as_of: asOf }, isPath);
  if (!r) return null;
  const ticks = ((r.ticks as Record<string, unknown>[] | undefined) ?? []).map((t) => ({
    at: String(t.at ?? ""),
    price: num(t.price_cwt) ?? 0,
    change: num(t.change),
    head: num(t.head) ?? 0,
    headChange: num(t.head_change),
    lots: num(t.lots) ?? 0,
    lotsAdded: num(t.lots_added) ?? 0,
    untimed: t.untimed === true,
    late: t.late === true,
    stepMinutes: num(t.step_minutes) ?? 0,
  }));
  return {
    slug: String(r.slug), code: String(r.code ?? ""), name: String(r.name ?? ""), asOf: String(r.as_of ?? ""),
    sunday: r.sunday === true, live: r.live === true, floor: num(r.floor),
    open: num(r.open_cwt), high: num(r.high_cwt), low: num(r.low_cwt), last: num(r.last_cwt),
    firstAt: r.first_at == null ? null : String(r.first_at), lastAt: r.last_at == null ? null : String(r.last_at),
    marks: num(r.marks), stepMinutes: num(r.step_minutes),
    unknownArrivalLots: num(r.unknown_arrival_lots) ?? 0, lateLots: num(r.late_lots) ?? 0,
    final: num(r.final_cwt), finalHead: num(r.final_head), finalLots: num(r.final_lots), finalBarns: num(r.final_barns),
    finalPublication: r.final_publication == null ? null : String(r.final_publication), finalRevision: num(r.final_revision),
    ticks,
  };
}

export function dailyCategory(q: Pick<DailyQuote, "isHeadline" | "cattleType">): DailyCategory {
  if (q.isHeadline) return "headline";
  return q.cattleType === "Heifers" ? "heifers" : "steers";
}

export async function fetchDailyQuotes(): Promise<DailyQuote[]> {
  await tickerAuthSettled();
  const rows = ((await rpc<Record<string, unknown>>("rb_daily_price_quotes", {})) ?? []).filter(shown);
  return rows.map((r) => ({
    slug: String(r.slug ?? ""),
    code: String(r.code ?? ""),
    name: String(r.name ?? ""),
    isHeadline: r.is_headline === true,
    cohort: String(r.cohort ?? ""),
    cattleType: r.cattle_type == null ? null : String(r.cattle_type),
    asOf: String(r.as_of ?? ""),
    settled: r.settled !== false,
    last: num(r.last_cwt),
    change: num(r.change),
    changePct: num(r.change_pct),
    open: num(r.open_cwt),
    high: num(r.high_cwt),
    low: num(r.low_cwt),
    prev: num(r.prev_cwt),
    prevAsOf: r.prev_as_of == null ? null : String(r.prev_as_of),
    wkAsOf: r.wk_as_of == null ? null : String(r.wk_as_of), wkCwt: num(r.wk_cwt),
    changeWk: num(r.change_wk), changeWkPct: num(r.change_wk_pct),
    wkHead: num(r.wk_head), headChgWkPct: num(r.head_chg_wk_pct),
    head: num(r.head) ?? 0,
    lots: num(r.lots) ?? 0,
    barns: num(r.barns) ?? 0,
    states: num(r.states) ?? 0,
    todayHead: num(r.today_head) ?? 0,
    todayLots: num(r.today_lots) ?? 0,
    todayPublication: (r.today_publication as DailyQuote["todayPublication"]) ?? null,
    floor: num(r.floor),
    status: String(r.status ?? ""),
  }));
}

export async function fetchDailyHistory(slug: string, days: DailyDays): Promise<DailyHistoryRow[]> {
  await tickerAuthSettled();
  const rows = (await rpc<Record<string, unknown>>("rb_daily_price_history", { p_slug: slug, p_days: String(days) })) ?? [];
  return rows.map((r) => ({
    asOf: String(r.as_of ?? ""),
    sunday: r.sunday === true,
    price: num(r.price_cwt),
    change: num(r.change),
    changePct: num(r.change_pct),
    prev: num(r.prev_cwt),
    open: num(r.open_cwt),
    high: num(r.high_cwt),
    low: num(r.low_cwt),
    head: num(r.head),
    lots: num(r.lots),
    barns: num(r.barns),
    states: num(r.states),
    headWkAgo: num(r.head_wk_ago),
    headChgWk: num(r.head_chg_wk),
    headChgWkPct: num(r.head_chg_wk_pct),
    publication: (String(r.publication ?? "NO_SALES") as DailyHistoryRow["publication"]),
    revision: num(r.revision),
    stepMinutes: num(r.step_minutes),
    unknownArrivalLots: num(r.unknown_arrival_lots) ?? 0,
    lateLots: num(r.late_lots) ?? 0,
    beforeCwt: num(r.before_cwt),
    revisedAt: r.revised_at == null ? null : String(r.revised_at),
    revisionReason: r.revision_reason == null ? null : String(r.revision_reason),
  }));
}

/** Every revision of a Daily Live Price final, before and after. */
export interface DailyCorrection {
  slug: string;
  code: string;
  name: string;
  asOf: string;
  revision: number;
  revisedAt: string;
  before: number | null;
  after: number | null;
  change: number | null;
  beforeHead: number | null;
  afterHead: number | null;
  reason: string | null;
}

export async function fetchDailyCorrections(days: 30 | 90 | 400 = 400): Promise<DailyCorrection[]> {
  const rows = (await rpc<Record<string, unknown>>("rb_daily_price_corrections", { p_days: String(days) })) ?? [];
  return rows.map((r) => ({
    slug: String(r.slug ?? ""), code: String(r.code ?? ""), name: String(r.name ?? ""), asOf: String(r.as_of ?? ""),
    revision: num(r.revision) ?? 0, revisedAt: String(r.revised_at ?? ""),
    before: num(r.before_cwt), after: num(r.after_cwt), change: num(r.change),
    beforeHead: num(r.before_head), afterHead: num(r.after_head),
    reason: r.reason == null ? null : String(r.reason),
  }));
}


/* ── The weekly grid (owner, 2026-10-08) ─────────────────────────────────────
   Every class across the last N sale days ending today, from
   rb_daily_price_board: the same finals and the same live reading as the
   daily table, plus the change against the same weekday a week earlier. */
export interface DailyBoardDay {
  asOf: string;
  sunday: boolean;
  /** Today, building. */
  live: boolean;
  price: number | null;
  /** Against the prior sale day that had a price. */
  change: number | null;
  /** Against the same weekday a week earlier ("Tuesday vs Tuesday"). Null when that day had no price. */
  changeWk: number | null;
  wkPrice: number | null;
  head: number | null;
  lots: number | null;
  publication: string;
  revision: number | null;
}
export interface DailyBoardClass {
  slug: string;
  code: string;
  name: string;
  cohort: string;
  cattleType: string | null;
  weightMin: number | null;
  weightMax: number | null;
  isHeadline: boolean;
  /** The live seven-day index — the quotable benchmark, the grid's last column. */
  indexCwt: number | null;
  /** When today's newest lot landed, or null before the first. */
  lastAt: string | null;
  days: DailyBoardDay[];
}

export async function fetchDailyBoard(days: 6 | 7 | 14 = 6): Promise<DailyBoardClass[]> {
  await tickerAuthSettled();
  const rows = ((await rpc<Record<string, unknown>>("rb_daily_price_board", { p_days: String(days) })) ?? []).filter(shown);
  return rows.map((r) => ({
    slug: String(r.slug ?? ""), code: String(r.code ?? ""), name: String(r.name ?? ""), cohort: String(r.cohort ?? ""),
    cattleType: r.cattle_type == null ? null : String(r.cattle_type),
    weightMin: num(r.weight_min), weightMax: num(r.weight_max), isHeadline: r.is_headline === true,
    indexCwt: num(r.index_cwt), lastAt: r.last_at == null ? null : String(r.last_at),
    days: (Array.isArray(r.days) ? (r.days as Record<string, unknown>[]) : []).map((d) => ({
      asOf: String(d.as_of ?? ""), sunday: d.sunday === true, live: d.live === true,
      price: num(d.price_cwt), change: num(d.change), changeWk: num(d.change_wk), wkPrice: num(d.wk_cwt),
      head: num(d.head), lots: num(d.lots), publication: String(d.publication ?? "NO_SALES"), revision: num(d.revision),
    })),
  }));
}
